import { SiteHeader } from "@/components/landing/SiteHeader";
import { SiteFooter } from "@/components/landing/SiteFooter";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";
import { cn } from "@/lib/utils";
import type { ProductConfigBuilder, Section } from "./product-types";
import { Hero } from "./sections/Hero";
import {
  AlcatrazBand,
  Breadcrumb,
  GlanceStats,
  ProductCta,
  ProductIntro,
  SpecsTable,
} from "./sections/Bands";
import {
  ConnectivityPillar,
  PerformancePillar,
  RemotePillar,
  SoftwarePillar,
} from "./sections/Pillars";

function renderSection(section: Section, index: number) {
  const key = `${section.kind}-${index}`;
  switch (section.kind) {
    case "hero":
      return <Hero key={key} section={section} />;
    case "glance":
      return <GlanceStats key={key} section={section} />;
    case "intro":
      return <ProductIntro key={key} section={section} />;
    case "performance":
      return <PerformancePillar key={key} section={section} />;
    case "connectivity":
      return <ConnectivityPillar key={key} section={section} />;
    case "software":
      return <SoftwarePillar key={key} section={section} />;
    case "remote":
      return <RemotePillar key={key} section={section} />;
    case "alcatraz":
      return <AlcatrazBand key={key} section={section} />;
    case "specs":
      return <SpecsTable key={key} section={section} />;
    case "cta":
      return <ProductCta key={key} section={section} />;
    default:
      return null;
  }
}

/**
 * Config-driven product page. Each product supplies a `build` function that
 * resolves copy/links into a `ProductConfig`; the engine renders the breadcrumb
 * and the ordered sections.
 */
export function ProductPage({ build }: { build: ProductConfigBuilder }) {
  const { messages, site, href } = useSite();
  const home = localizeUrl("/", site.locale, href);
  const config = build({
    messages: messages as unknown as Record<string, string>,
    urls: {
      home,
      products: localizeUrl("/products", site.locale, href),
      contactSales: localizeUrl("/contacts?inquiry=sales#inquiry-form", site.locale, href),
      distributors: localizeUrl("/distributors", site.locale, href),
      compare: localizeUrl("/products", site.locale, href),
      alcatraz: localizeUrl("/solutions/alcatraz-dms", site.locale, href),
      remoteApp: localizeUrl("/solutions/inext-remote", site.locale, href),
      accessories: localizeUrl("/products/accessories", site.locale, href),
    },
  });

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />
      <main className={cn("flex-1", config.mainClassName)}>
        <Breadcrumb breadcrumb={config.breadcrumb} />
        {config.sections.map(renderSection)}
      </main>
      <SiteFooter />
    </div>
  );
}
